"use client";
//? Library Imports ------------------------------------------------------------>
import { useSelector } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import {
  Col,
  Row,
  Radio,
  Tag,
  Space,
  Skeleton,
  Typography,
  message,
} from "antd";
import { useRouter } from "next/navigation";

//? Assets --------------------------------------------------------------------->
import { BiSolidPencil } from "react-icons/bi";
import { ArrowLeftOutlined, CloseOutlined } from "@ant-design/icons";

//? Modules -------------------------------------------------------------------->
import AccountStatus from "@/functions/Cookies/Cookies";
import CreateNewAddress from "./Modules/CreateNewAddress/CreateNewAddress";
import DynamicButton from "@/components/shared/Buttons/DynamicButton/DynamicButton";
import { customerLogout } from "@/functions/Cookies/Cookies";

//? Redux Reducers ------------------------------------------------------------->
import { useDispatch } from "react-redux";
import { logoutReducer } from "@/redux/Slices/accountSlice";
//? Library Constants ---------------------------------------------------------->
const { Text } = Typography;

export default function AddressBook() {
  const {
    loggedIn,
    comverse_customer_id,
    comverse_customer_email,
    comverse_customer_token,
  } = AccountStatus();

  //? Redux States --------------------------------------------------------------->
  const defaultCurrency = useSelector(
    (state) => state.multiLocation.defaultCurrency
  );
  const StoreId = useSelector(
    (state) => state?.multiLocation?.defaultCountryId
  );
  const dispatch = useDispatch();
  const router = useRouter();
  const [value, setValue] = useState(null);
  const [Loading, setLoading] = useState(true);
  const [AddressList, setAddressList] = useState([]);
  const [AddNewAddress, setAddNewAddress] = useState(false);
  const [FormType, setFormType] = useState("create");
  const [SelectedAddress, setSelectedAddress] = useState({});

  const selectAddress = (e) => {
    setValue(e.target.value);
  };

  const fetchCustomerAddresses = useCallback(async () => {
    if (comverse_customer_token) {
      try {
        const payload = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: comverse_customer_token,
          },
        };

        const res = await fetch(
          process.env.NEXT_PUBLIC_BACKEND +
            `/storefront/customer_address?storefront=${StoreId}`,
          payload
        );

        const CustomerAddresses = await res.json();

        if (CustomerAddresses.detail === "Invalid auth token") {
          customerLogout();
          dispatch(logoutReducer());
          router.push("/login");
        }

        if (!res.ok) {
          throw new Error("Customer Address Api Failed!");
        }

        setAddressList(CustomerAddresses);
        setValue(CustomerAddresses[0]?.id);

        setLoading(false);

        return CustomerAddresses;
      } catch (error) {
        console.error("Customer Address Api Failed!", error);
        setTimeout(() => {
          setLoading(false);
        }, 1000);
        return null;
      }
    }
  }, [StoreId, comverse_customer_token]);

  const removeAddress = async (id) => {
    if (comverse_customer_token && id) {
      try {
        setLoading(true);
        const headers = {
          "Content-Type": "application/json",
        };
        if (comverse_customer_token) {
          headers.Authorization = comverse_customer_token;
        }

        const body = {
          id: id,
        };

        const payload = {
          method: "DELETE",
          headers: headers,
          body: JSON.stringify(body),
        };

        const res = await fetch(
          process.env.NEXT_PUBLIC_BACKEND +
            `/storefront/customer_address?storefront=${StoreId}`,
          payload
        );
        const data = await res.json();

        if (!res.ok) {
          throw new Error("Api Failed!");
        } else {
          setLoading(false);
          message.success(data?.detail);
          fetchCustomerAddresses();
          return data;
        }
      } catch (error) {
        console.error("Error:", error);

        setTimeout(() => {
          setLoading(false);
        }, 1000);
        return null;
      }
    }
  };

  const editAddress = async () => {
    if (comverse_customer_token) {
      try {
        const payload = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: comverse_customer_token,
          },
        };

        const res = await fetch(
          process.env.NEXT_PUBLIC_BACKEND +
            `/storefront/customer_address?id=${value}&storefront=${StoreId}`,
          payload
        );

        const CustomerAddress = await res.json();

        if (!res.ok) {
          throw new Error("Customer Address Api Failed!");
        }

        if (CustomerAddress) {
          setLoading(false);
          setFormType("edit");
          setAddNewAddress(true);
          setSelectedAddress(CustomerAddress);
        }

        return CustomerAddress;
      } catch (error) {
        console.error("Customer Address Api Failed!", error);
        setTimeout(() => {
          setLoading(false);
        }, 1000);
        return null;
      }
    }
  };

  useEffect(() => {
    fetchCustomerAddresses();
  }, [fetchCustomerAddresses]);

  return (
    <Col className="AddressBook padding10">
      {AddNewAddress ? (
        <Col span={24}>
          <CreateNewAddress
            FormType={FormType}
            setFormType={setFormType}
            SelectedAddress={SelectedAddress}
            setAddNewAddress={setAddNewAddress}
            fetchCustomerAddresses={fetchCustomerAddresses}
          />
        </Col>
      ) : (
        <Col>
          <Col span={24}>
            <Space size="middle" className="flexBetween width100">
              <Text className="font14Bold poppinsMedium">Address Book</Text>
              <DynamicButton
                name="Edit"
                className="editBtn"
                icon={<BiSolidPencil />}
                function={() => editAddress()}
              />
            </Space>
          </Col>

          {AddressList?.length > 0 && (
            <Col span={24}>
              <Col className="marginTop15">
                <Col className="addressCarddd">
                  <Radio.Group onChange={selectAddress} value={value}>
                    {AddressList?.map((address, index) => {
                      return (
                        <Radio
                          key={index}
                          value={address?.id}
                          className="addressCard positionRelative"
                        >
                          <CloseOutlined
                            style={{
                              top: "10px",
                              right: "10px",
                              cursor: "pointer",
                              position: "absolute",
                            }}
                            onClick={() => removeAddress(address?.id)}
                          />
                          <Col className="flexBetween">
                            <Col>
                              <Text className="font14">
                                {address?.full_name}&nbsp;
                                {address?.primary_address ? (
                                  <span
                                    style={{
                                      color: "#B7B7B7",
                                      fontSize: "12px",
                                    }}
                                  >
                                    (Default)
                                  </span>
                                ) : null}
                              </Text>
                              <br />
                              <Text className="font14Bold">
                                {address?.address}
                              </Text>
                            </Col>

                            {/* <Col
                              style={{
                                position: "absolute",
                                right: "0",
                                top: "0",
                              }}
                            >
                              <Tag className="addressTag borderNone">
                                {address?.tag}
                              </Tag>
                            </Col> */}
                          </Col>
                        </Radio>
                      );
                    })}
                  </Radio.Group>
                </Col>
              </Col>
            </Col>
          )}

          <Col className="marginTop15 flexCenter">
            <DynamicButton
              className="addNew"
              name="Add New Address"
              function={() => setAddNewAddress(true)}
            />
          </Col>
        </Col>
      )}
    </Col>
  );
}
